import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminCompanyService } from '../services/admin.company.service'
import type { CompanyMemberDocument } from '@/types'
import type { MemberFormValues } from '../types'

type Member = CompanyMemberDocument & { id: string }

interface UseAdminCompanyResult {
  members: Member[]
  loading: boolean
  error: string | null
  createMember: (data: MemberFormValues) => Promise<string>
  updateMember: (id: string, data: Partial<MemberFormValues>) => Promise<void>
  deleteMember: (id: string) => Promise<void>
  refresh: () => void
}

function toSlug(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export function useAdminCompany(): UseAdminCompanyResult {
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => {
    setTick((t) => t + 1)
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminCompanyService
      .getAllMembers()
      .then(setMembers)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load members'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createMember = useCallback(async (data: MemberFormValues): Promise<string> => {
    const id = await adminCompanyService.createMember(data)
    const now = new Date()
    setMembers((prev) => [
      ...prev,
      {
        id,
        name: data.name,
        slug: toSlug(data.name),
        bio: data.bio,
        photoUrl: '',
        squarePhotoUrl: '',
        portraitPhotoUrl: '',
        roles: data.roles,
        isActive: data.isActive,
        isPublished: data.isPublished,
        seasonNumbers: data.seasonNumbers,
        order: data.order,
        socialLinks: {
          instagram: data.socialLinks.instagram || '',
          website: data.socialLinks.website || '',
        },
        availableForHire: data.availableForHire ?? false,
        ...(data.email ? { email: data.email } : {}),
        ...(data.photoCredit ? { photoCredit: data.photoCredit } : {}),
        createdAt: now,
        updatedAt: now,
      },
    ])
    return id
  }, [])

  const updateMember = useCallback(
    async (id: string, data: Partial<MemberFormValues>): Promise<void> => {
      let snapshot: Member | undefined
      setMembers((prev) => {
        snapshot = prev.find((m) => m.id === id)
        return prev.map((m) => (m.id === id ? { ...m, ...data } : m))
      })
      try {
        await adminCompanyService.updateMember(id, data)
      } catch (err) {
        if (snapshot) {
          const saved = snapshot
          setMembers((prev) => prev.map((m) => (m.id === id ? saved : m)))
        }
        throw err
      }
    },
    []
  )

  const deleteMember = useCallback(async (id: string): Promise<void> => {
    let snapshot: Member | undefined
    setMembers((prev) => {
      snapshot = prev.find((m) => m.id === id)
      return prev.filter((m) => m.id !== id)
    })
    try {
      await adminCompanyService.deleteMember(id)
    } catch (err) {
      if (snapshot) {
        const saved = snapshot
        setMembers((prev) => [...prev, saved].sort((a, b) => a.order - b.order))
      }
      throw err
    }
  }, [])

  return { members, loading, error, createMember, updateMember, deleteMember, refresh }
}
